import { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import KPIWidget from './components/KPIWidget';

import SystemLogFeed from './components/SystemLogFeed';
import FleetStatusPanel from './components/FleetStatusPanel';
import ThreatWatchPanel from './components/ThreatWatchPanel';
import GoldenListHealthKPI from './components/GoldenListHealthKPI';

const SystemOverview = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [kpiData, setKpiData] = useState({
    totalSent: 1247893,
    avgDeliverability: 98.7,
    avgOpenRate: 24.3,
    activeServers: 24,
    goldenListSize: 8920,
    goldenListEngagement: 95,
    goldenListTrend: '+3.2%'
  });

  const [servers, setServers] = useState([
    {
      id: "srv-prod-01",
      name: "Production Mail Server 01",
      ip: "192.168.1.101",
      status: "Online",
      purpose: "Production",
      reputation: 95,
      dailySent: 45230,
      dailyLimit: 50000,
      blacklistCount: 0
    },
    {
      id: "srv-prod-02",
      name: "Production Mail Server 02",
      ip: "192.168.1.102",
      status: "Online",
      purpose: "Production",
      reputation: 92,
      dailySent: 38750,
      dailyLimit: 50000,
      blacklistCount: 0
    },
    {
      id: "srv-prod-03",
      name: "Production Mail Server 03",
      ip: "192.168.1.103",
      status: "Warning",
      purpose: "Production",
      reputation: 78,
      dailySent: 42100,
      dailyLimit: 50000,
      blacklistCount: 1
    },
    {
      id: "srv-prod-04",
      name: "Production Mail Server 04",
      ip: "192.168.1.104",
      status: "Online",
      purpose: "Production",
      reputation: 88,
      dailySent: 31500,
      dailyLimit: 40000,
      blacklistCount: 0
    },
    {
      id: "srv-prod-05",
      name: "Production Mail Server 05",
      ip: "192.168.1.105",
      status: "Online",
      purpose: "Production",
      reputation: 91,
      dailySent: 28900,
      dailyLimit: 35000,
      blacklistCount: 0
    },
    {
      id: "srv-canary-01",
      name: "Canary Mail Server 01",
      ip: "192.168.1.201",
      status: "Warning",
      purpose: "Canary",
      reputation: 65,
      dailySent: 15200,
      dailyLimit: 25000,
      blacklistCount: 2
    },
    {
      id: "srv-quarantine-01",
      name: "Quarantine Mail Server 01",
      ip: "192.168.1.202",
      status: "Quarantined",
      purpose: "Quarantine",
      reputation: 45,
      dailySent: 0,
      dailyLimit: 25000,
      blacklistCount: 3
    },
    {
      id: "srv-sanitizer-01",
      name: "Sanitizer Mail Server 01",
      ip: "192.168.1.301",
      status: "Online",
      purpose: "Sanitizer",
      reputation: 52,
      dailySent: 5400,
      dailyLimit: 10000,
      blacklistCount: 1
    }
  ]);

  const [systemLogs, setSystemLogs] = useState([
    {
      timestamp: new Date(Date.now() - 60000),
      severity: "Critical",
      source: "Threat_Watch",
      serverId: "srv-quarantine-01",
      message: "SERVER QUARANTINED: Policy block received from Gmail for contact user@example.com. Server srv-quarantine-01 has been taken offline pending review"
    },
    {
      timestamp: new Date(Date.now() - 90000),
      severity: "Warning",
      source: "Canary_Simulator",
      serverId: "srv-canary-01",
      message: "Canary simulation failed for campaign 'Re-engagement Q1'. Complaint rate: 0.15% (threshold: 0.1%). Main campaign auto-paused"
    },
    {
      timestamp: new Date(Date.now() - 120000),
      severity: "Info",
      source: "AI_Governor",
      serverId: "srv-prod-01",
      message: "Daily sending limit adjusted from 45000 to 50000 based on reputation score improvement"
    },
    {
      timestamp: new Date(Date.now() - 180000),
      severity: "Success",
      source: "Health_Monitor",
      serverId: "srv-prod-02",
      message: "SPF and DKIM records validated successfully. All authentication checks passed"
    },
    {
      timestamp: new Date(Date.now() - 240000),
      severity: "Warning",
      source: "Blacklist_Scanner",
      serverId: "srv-prod-03",
      message: "Server detected on Spamhaus ZEN blacklist. Initiating automated delisting procedure"
    },
    {
      timestamp: new Date(Date.now() - 300000),
      severity: "Info",
      source: "Campaign_Manager",
      serverId: "srv-prod-04",
      message: "Campaign 'Q1 Newsletter' completed. Delivered: 48,523 | Bounced: 127 | Opened: 11,845"
    },
    {
      timestamp: new Date(Date.now() - 360000),
      severity: "Critical",
      source: "Connection_Monitor",
      serverId: "srv-backup-02",
      message: "Server connection lost. Unable to establish SSH connection for health check"
    },
    {
      timestamp: new Date(Date.now() - 420000),
      severity: "Success",
      source: "AI_Governor",
      serverId: "srv-prod-05",
      message: "Reputation score increased to 91%. Server promoted to high-priority sending pool"
    },
    {
      timestamp: new Date(Date.now() - 480000),
      severity: "Warning",
      source: "Rate_Limiter",
      serverId: "srv-backup-01",
      message: "Approaching daily sending limit. 85% capacity reached with 4 hours remaining"
    },
    {
      timestamp: new Date(Date.now() - 540000),
      severity: "Info",
      source: "DNS_Manager",
      serverId: "srv-test-01",
      message: "PTR record updated successfully. Reverse DNS now resolves to mail.testdomain.com"
    },
    {
      timestamp: new Date(Date.now() - 600000),
      severity: "Success",
      source: "Fleet_Provisioner",
      serverId: "srv-prod-01",
      message: "Server configuration backup completed. All Postfix settings archived to secure storage"
    },
    {
      timestamp: new Date(Date.now() - 660000),
      severity: "Info",
      source: "Content_Analyzer",
      serverId: "srv-prod-02",
      message: "Campaign content scored 2.1 on SpamAssassin. Predicted inbox rate: 96.8%"
    }
  ]);

  useEffect(() => {
    const logInterval = setInterval(() => {
      const newLog = {
        timestamp: new Date(),
        severity: ["Info", "Success", "Warning"]?.[Math.floor(Math.random() * 3)],
        source: ["AI_Governor", "Health_Monitor", "Campaign_Manager", "Rate_Limiter"]?.[Math.floor(Math.random() * 4)],
        serverId: servers?.[Math.floor(Math.random() * servers?.length)]?.id,
        message: [
          "Routine health check completed successfully",
          "Campaign delivery metrics updated",
          "Server reputation score recalculated",
          "Daily sending quota refreshed"
        ]?.[Math.floor(Math.random() * 4)]
      };

      setSystemLogs(prev => [...prev, newLog]?.slice(-20));
    }, 15000);

    return () => clearInterval(logInterval);
  }, [servers]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main 
        className={`pt-20 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:pl-[80px]' : 'lg:pl-[280px]'
        }`}
      >
        <div className="p-4 md:p-6 lg:p-8">
          <div className="mb-6 md:mb-8">
            <Breadcrumbs />
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground mt-4 mb-2">
              War Room Dashboard
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Zero-Burn Core Architecture - Real-time fleet monitoring and threat detection
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <KPIWidget
              title="Total Sent Today"
              value={kpiData?.totalSent?.toLocaleString()}
              unit=""
              trend="up"
              trendValue="+12.5%"
              icon="Send"
              iconColor="text-primary"
            />
            <KPIWidget
              title="Average Deliverability"
              value={kpiData?.avgDeliverability}
              unit="%"
              trend="up"
              trendValue="+2.1%"
              icon="TrendingUp"
              iconColor="text-success"
            />
            <KPIWidget
              title="Average Open Rate"
              value={kpiData?.avgOpenRate}
              unit="%"
              trend="down"
              trendValue="-0.8%"
              icon="Mail"
              iconColor="text-warning"
            />
            <KPIWidget
              title="Active Servers"
              value={kpiData?.activeServers}
              unit=""
              trend="up"
              trendValue="+2"
              icon="Server"
              iconColor="text-primary"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <FleetStatusPanel servers={servers} />
            </div>
            <div>
              <GoldenListHealthKPI 
                goldenListData={{
                  size: kpiData?.goldenListSize,
                  engagement: kpiData?.goldenListEngagement,
                  trend: kpiData?.goldenListTrend
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
            <ThreatWatchPanel threats={systemLogs?.filter(log => 
              log?.severity === 'Critical' || log?.severity === 'Warning'
            )} />
            <div className="h-[600px]">
              <SystemLogFeed logs={systemLogs} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SystemOverview;